<template>
  <div class="vs-con-loading__container relative" id="open_street_map">
    <!--Leaflet Map-->
    <l-map
      :zoom="zoom"
      :center="center"
      :options="mapOptions"
      @update:center="centerUpdate"
      @update:zoom="zoomUpdate"
      @click="addMarker"
      :style="`height: ${height}px`"
      ref="map"
      :class="store.state.windowWidth <= 576 ? 'osm-w-full' : ''"
    >
      <!--Clear Select Marker Button-->
      <l-control position="bottomleft" v-if="!viewOnly">
        <vs-button
          @click="clearMarker"
          radius
          color="warning"
          type="filled"
          icon-pack="feather"
          icon="icon-x"
        />
      </l-control>

      <!--Clear Select Marker Button-->
      <l-control position="bottomleft" v-if="!viewOnly">
        <vs-button
          @click="setMarkerBaseOnMyLocation"
          radius
          color="green"
          type="filled"
          icon-pack="feather"
          icon="icon-map-pin"
        />
      </l-control>

      <!--Copy Right Text-->
      <l-tile-layer :url="url" />

      <!--Current Location Marker-->
      <l-marker
        :icon="currentLocationPin()"
        v-if="!viewOnly && !selectMarker"
        :lat-lng="currentLocationMarker"
      />

      <!--Select Marker-->
      <l-marker :icon="getSelectionIcon()" v-if="selectMarker" :lat-lng="selectMarker" />

      <!--Prop Markers-->
      <l-marker :icon="viewPin()" v-for="(marker, index) in markers" :key="index" :lat-lng="marker.latlng">
        <l-popup v-if="marker.name" :content="marker.name" />
      </l-marker>

      <!--Prop Polygons-->
      <l-polygon
        v-for="(polygon, index) in polygons"
        :key="index"
        :lat-lngs="polygon.latlngs"
        :color="polygon.color ? polygon.color : '#ff9'"
        :fill-color="polygon.color ? polygon.color : '#ff9'"
        :opacity="0"
        :fill-opacity="polygonOpacity"
      />

      <!--Prop GeoJson-->
      <l-geo-json v-if="geojson" :geojson="geojson" />
    </l-map>
    <div class="osm-w-full-cover" :style="`height: ${height}px`" v-if="store.state.windowWidth <= 576"></div>
    <div v-if="disabled" class="w-full h-full absolute top-0" style="z-index: 999;"></div>
  </div>
</template>

<script>
import { divIcon } from 'leaflet'
import { LMap, LTileLayer, LMarker, LPolygon, LPopup, LGeoJson, LControl } from 'vue2-leaflet'
import 'leaflet/dist/leaflet.css'
import { getCurrentPosition } from '@/location'
import store from '@/store/store'

export default {
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LPolygon,
    LPopup,
    LGeoJson,
    LControl
  },
  props: {
    viewOnly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    clearSelection: {
      type: Boolean,
      default: false
    },
    height: {
      type: Number,
      default: 300
    },
    center: {
      type: [Array, Object],
      default: () => [30.293338, 57.070155]
    },
    zoom: {
      type: Number,
      default: 12
    },
    markers: {
      type: Array,
      default: () => []
    },
    validateSelection: {
      type: Boolean,
      default: false
    },
    polygons: {
      type: Array,
      default: () => []
    },
    polygonOpacity: {
      type: Number,
      default: 0.2
    },
    geojson: {
      type: String,
      default: ''
    },
    disableFit: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      store,
      selectMarker: null,
      overpassLoading: false,
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      mapOptions: {
        zoomDelta: 0.5,
        zoomSnap: 0.2
      },
      currentLocationMarker: { lat: 0, lng: 0 }
    }
  },
  watch: {
    markers(val) {
      if (!this.disableFit && val) this.$refs.map.mapObject.fitBounds(val.map(m => m.latlng))
    },
    clearSelection(val) {
      if (val) {
        this.selectMarker = null
      }
    }
  },
  methods: {
    getSelectionIcon() {
      const icon = {
        size: 30,
        color: '#0eae02',
        sign:
          'm92.999997,112.252969l12.464449,-14.01669l34.146157,38.379976l56.920543,-63.966627l12.468855,14.006784l-69.389397,77.993223'
      }
      if (this.validateSelection && !this.isMarkerValid()) {
        icon.color = '#c11a1a'
        icon.sign =
          'm200.931816,159.451914l-34.340024,-34.340037l34.333782,-34.340037l-17.650597,-17.663087l-34.340024,34.340037l-34.340024,-34.340037l-17.650597,17.663087l34.333782,34.340037l-34.340024,34.340037l17.66308,17.656846l34.333782,-34.340037l34.333782,34.340037'
      }
      return divIcon({
        className: 'my-custom-pin',
        html: `
          <svg width="${icon.size}" height="${icon.size}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 300" >
            <g>
              <path d="m148.27352,6.85685c97.02946,-2.15683 221.72984,123.0358 2.74027,293.14312c-218.98957,-170.10732 -99.76974,-290.98629 -2.74027,-293.14312z" fill-opacity="1" stroke-opacity="0.7" stroke-width="5" stroke="${icon.color}" fill="${icon.color}"/>
            </g>
            <g>
              <path stroke="#000" d="${icon.sign}" fill-opacity="null" stroke-opacity="null" stroke-width="0" fill="#fff"/>
            </g>
          </svg>`,
        iconAnchor: [icon.size / 2 - (this.$vs.rtl ? icon.size - 12 : 0), icon.size]
      })
    },
    viewPin() {
      const icon = {
        size: 40,
        color: '#1135e8',
        sign:
          'm92.999997,112.252969l12.464449,-14.01669l34.146157,38.379976l56.920543,-63.966627l12.468855,14.006784l-69.389397,77.993223'
      }
      return divIcon({
        className: 'my-custom-pin',
        html: `
          <svg width="${icon.size}" height="${icon.size}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 300" >
          <defs>
            <mask id="hole">
              <rect width="100%" height="100%" fill="white"/>
              <circle r="60" cx="150" cy="130" fill="black"/>
            </mask>
          </defs>
            <g mask="url(#hole)">
              <path d="m148.27352,6.85685c97.02946,-2.15683 221.72984,123.0358 2.74027,293.14312c-218.98957,-170.10732 -99.76974,-290.98629 -2.74027,-293.14312z" fill-opacity="1" stroke-opacity="0.7" stroke-width="5" stroke="${icon.color}" fill="${icon.color}"/>
            </g>
          </svg>`,
        iconAnchor: [icon.size / 2 - (this.$vs.rtl ? icon.size - 12 : 0), icon.size]
      })
    },
    currentLocationPin() {
      const icon = {
        size: 25,
        color: '#4be514',
        sign:
          'm92.999997,112.252969l12.464449,-14.01669l34.146157,38.379976l56.920543,-63.966627l12.468855,14.006784l-69.389397,77.993223'
      }
      return divIcon({
        className: 'my-custom-pin',
        html: `
          <svg width="${icon.size}" height="${icon.size}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 300" >
          <defs>
            <mask id="hole">
              <rect width="100%" height="100%" fill="white"/>
              <circle r="60" cx="150" cy="130" fill="black"/>
            </mask>
          </defs>
            <g mask="url(#hole)">
              <path d="m148.27352,6.85685c97.02946,-2.15683 221.72984,123.0358 2.74027,293.14312c-218.98957,-170.10732 -99.76974,-290.98629 -2.74027,-293.14312z" fill-opacity="1" stroke-opacity="0.7" stroke-width="5" stroke="${icon.color}" fill="${icon.color}"/>
            </g>
          </svg>`,
        iconAnchor: [icon.size / 2 - (this.$vs.rtl ? icon.size - 12 : 0), icon.size]
      })
    },
    zoomUpdate(zoom) {
      this.currentZoom = zoom
    },
    centerUpdate(center) {
      this.currentCenter = center
    },
    emitOverpassData(lat, lon) {
      this.$vs.loading({
        container: '#open_street_map',
        scale: 0.45
      })

      this.$emit('input', { opStatus: 'loading' })
      this.$http
        .get(`overpass/${lat}/${lon}`)
        .then(res => {
          this.$emit('input', { opStatus: 'loaded', location: { lat, lon }, ...res.data })
          this.overpassLoading = false
        })
        .catch(() => {
          this.$emit('input', { opStatus: 'error', location: { lat, lon } })
          this.overpassLoading = false
        })
        .finally(() => this.$vs.loading.close('#open_street_map > .con-vs-loading'))
      this.overpassLoading = true
    },
    // eslint-disable-next-line no-unused-vars
    async addMarker(event, index) {
      if (this.overpassLoading || this.viewOnly) return
      this.selectMarker = event.latlng
      if (this.validateSelection) {
        if (this.isMarkerValid()) {
          this.emitOverpassData(event.latlng.lat, event.latlng.lng)
        } else {
          this.$emit('input', { opStatus: 'invalid' })
        }
      } else {
        this.emitOverpassData(event.latlng.lat, event.latlng.lng)
      }
    },
    clearMarker() {
      this.selectMarker = null
      this.overpassLoading = false
      this.$emit('input', { opStatus: 'clear' })
    },
    setMarkerBaseOnMyLocation() {
      getCurrentPosition()
      const location = store.state.AppActiveUser.location
      if (location) {
        this.addMarker({ latlng: { lat: location.lat, lng: location.lon } })
        this.$refs.map.mapObject.flyTo(this.selectMarker)
      }
    },
    isMarkerValid() {
      let outOffPoly = 0
      for (const poly of this.polygons) {
        if (!this.isMarkerInsidePolygon(this.selectMarker, poly.latlngs)) {
          outOffPoly += 1
        }
      }
      // check if marker is out of all polygons
      return outOffPoly < this.polygons.length
    },
    isMarkerInsidePolygon(marker, poly) {
      const polyPoints = poly
      const x = marker.lat,
        y = marker.lng

      let inside = false
      for (let i = 0, j = polyPoints.length - 1; i < polyPoints.length; j = i++) {
        const xi = polyPoints[i].lat,
          yi = polyPoints[i].lng
        const xj = polyPoints[j].lat,
          yj = polyPoints[j].lng

        const intersect = yi > y !== yj > y && x < (xj - xi) * ((y - yi) / (yj - yi)) + xi
        if (intersect) inside = !inside
      }

      return inside
    }
  },
  mounted() {
    // Fix Tile Loading Bug
    window.dispatchEvent(new Event('resize'))

    this.$emit('input', { opStatus: 'clear' })

    const addresses = this.$store.state.AppActiveUser.addresses

    if (!this.disableFit && this.markers.length) {
      this.$refs.map.mapObject.fitBounds(
        this.markers.map(m => m.latlng),
        { padding: [50, 50] }
      )
    } else if (addresses && !this.viewOnly) {
      if (addresses.length) this.$refs.map.mapObject.panTo(addresses[addresses.length - 1].location)
    }
  }
}
</script>

<style lang="scss">
/* prettier-ignore */
[dir=rtl] {
  .leaflet-pane,
  .leaflet-tile,
  .leaflet-marker-icon,
  .leaflet-marker-shadow,
  .leaflet-tile-container,
  .leaflet-tooltip,
  .leaflet-pane > svg,
  .leaflet-pane > canvas,
  .leaflet-zoom-box,
  .leaflet-image-layer,
  .leaflet-layer {
    right: unset !important;
    left: 0 !important;
  }
}
.osm-w-full {
  position: absolute;
  z-index: 100;
  left: 0;
  right: 0;
}
</style>
